/**
 * Two-step delete confirmation for /v1/admin/meters/{id}.
 *
 * Step 1: confirm the destructive action with explicit copy.
 * Step 2: if the API returns 409 (`meter_has_readings` or `meter_in_use`)
 *         we *do not* surface a generic error toast. Instead we morph the
 *         modal in-place into a "retire instead" prompt with a one-click
 *         path that PATCHes status="retired". This matches §9.4's stance
 *         that retire is the everyday operator tool and DELETE is the
 *         exception.
 *
 * `mapping_in_use` returns extra guidance: the operator must drop the
 * external-ref row first (cookbook §9.2) before delete can proceed.
 * Retire still works without that step, so we keep it as the default action.
 */
import { Alert, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { IconAlertTriangle, IconArchive } from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { ApiError } from "@/api/client";
import { useDeleteMeter, useUpdateMeter } from "@/api/queries";
import type { AdminMeter } from "@/api/types";

export type MeterDeleteConfirmProps = {
  opened: boolean;
  onClose: () => void;
  meter: AdminMeter | null;
  onSuccess?: () => void;
};

type RetirableConflict = {
  code: "meter_has_readings" | "meter_in_use";
  message: string;
};

function asRetirableConflict(error: unknown): RetirableConflict | null {
  if (error instanceof ApiError && error.status === 409) {
    if (error.code === "meter_has_readings" || error.code === "meter_in_use") {
      return { code: error.code, message: error.message };
    }
  }
  return null;
}

export function MeterDeleteConfirm({
  opened,
  onClose,
  meter,
  onSuccess,
}: MeterDeleteConfirmProps) {
  const deleteMutation = useDeleteMeter();
  const retireMutation = useUpdateMeter();
  const [conflict, setConflict] = useState<RetirableConflict | null>(null);

  // Reset transient state when the modal is reopened against a new target.
  useEffect(() => {
    if (opened) {
      setConflict(null);
      deleteMutation.reset();
      retireMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, meter?.id]);

  if (!meter) return null;

  const isAlreadyRetired = meter.status === "retired";

  const handleDelete = async () => {
    setConflict(null);
    try {
      await deleteMutation.mutateAsync(meter.id);
      onSuccess?.();
      onClose();
    } catch (err) {
      const c = asRetirableConflict(err);
      if (c) {
        setConflict(c);
        return;
      }
      // anything else stays visible through deleteMutation.error below
    }
  };

  const handleRetire = async () => {
    try {
      await retireMutation.mutateAsync({
        id: meter.id,
        body: { status: "retired" },
      });
      onSuccess?.();
      onClose();
    } catch {
      // keep the modal open and surface retireMutation.error inline
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={conflict ? "Retire this meter instead?" : "Delete meter"}
      centered
      size="md"
    >
      <Stack>
        {conflict ? (
          <RetireInsteadPrompt conflict={conflict} meter={meter} />
        ) : (
          <Stack gap="xs">
            <Text>
              Permanently delete{" "}
              <Text component="span" fw={600}>
                {meter.display_name}
              </Text>{" "}
              ({meter.site_slug}/{meter.slug})?
            </Text>
            <Text c="dimmed" size="sm">
              This is only allowed if there are no readings or external mappings
              still pointing at it. For real meters, retire (status →
              <Text component="span" ff="monospace">
                {" retired"}
              </Text>
              ) is almost always the right call.
            </Text>
          </Stack>
        )}

        {!conflict && deleteMutation.error && !asRetirableConflict(deleteMutation.error) && (
          <Alert color="red" variant="light" title="Delete failed">
            {deleteMutation.error instanceof ApiError
              ? `${deleteMutation.error.code}: ${deleteMutation.error.message}`
              : "Unknown error"}
          </Alert>
        )}
        {retireMutation.error && (
          <Alert color="red" variant="light" title="Retire failed">
            {retireMutation.error instanceof ApiError
              ? `${retireMutation.error.code}: ${retireMutation.error.message}`
              : "Unknown error"}
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          {!conflict ? (
            <Button
              color="red"
              loading={deleteMutation.isPending}
              onClick={handleDelete}
            >
              Delete
            </Button>
          ) : (
            !isAlreadyRetired && (
              <Button
                color="orange"
                leftSection={<IconArchive size={16} />}
                loading={retireMutation.isPending}
                onClick={handleRetire}
              >
                Retire instead
              </Button>
            )
          )}
        </Group>
      </Stack>
    </Modal>
  );
}

function RetireInsteadPrompt({
  conflict,
  meter,
}: {
  conflict: RetirableConflict;
  meter: AdminMeter;
}) {
  const isAlreadyRetired = meter.status === "retired";
  const icon = <IconAlertTriangle size={18} />;

  if (conflict.code === "meter_has_readings") {
    return (
      <Alert color="orange" variant="light" icon={icon} title="Readings exist for this meter">
        <Stack gap="xs">
          <Text size="sm">
            <Text component="span" fw={600}>
              {meter.display_name}
            </Text>{" "}
            has telemetry history that we don't want to silently drop. Retire
            it instead — that hides it from operator views while preserving
            the readings hypertable.
          </Text>
          {isAlreadyRetired && (
            <Text size="sm" c="dimmed">
              Status is already <Text component="span" ff="monospace">retired</Text>.
              No further action is needed; close this dialog.
            </Text>
          )}
        </Stack>
      </Alert>
    );
  }

  return (
    <Alert color="orange" variant="light" icon={icon} title="External mapping still points here">
      <Stack gap="xs">
        <Text size="sm">
          A row in <Text component="span" ff="monospace">meter_external_refs</Text> is still
          mapped to <Text component="span" fw={600}>{meter.display_name}</Text>. Drop that
          mapping first (admin cookbook §9.2: <Text component="span" ff="monospace">DELETE
          /v1/admin/meter-external-refs/&#123;ref_id&#125;</Text>) before retrying the delete.
        </Text>
        <Text size="sm">
          For most operator workflows, retiring the meter is enough — the
          mapping can stay and the worker will keep writing into a
          <Text component="span" ff="monospace"> retired </Text> meter without complaining.
        </Text>
      </Stack>
    </Alert>
  );
}
